import type {
  ResolutionAt,
  ResolutionConfig,
  ResolutionSnapshot,
  ResolutionThresholdDecayPolicy,
} from '../conviction/interfaces.js';
import { projectFirstTouchResolution } from './first-touch-projection.js';
import { resolutionThresholdFloorPpb } from './resolution-threshold.js';

export type {
  ResolutionAt,
  ResolutionConfig,
  ResolutionSnapshot,
} from '../conviction/interfaces.js';

/**
 * Finds the earliest whole chain-clock second whose exact, unrounded
 * conviction numerators meet the on-chain staircase threshold.
 *
 * Work is bounded by the finite threshold-drop checkpoints and binary searches
 * over monotonic conviction-share segments. There is no calendar horizon.
 */
export function projectResolutionAt(
  snapshot: ResolutionSnapshot,
  config: ResolutionConfig,
): ResolutionAt | null {
  const policy = toPolicy(config);
  return policy === null ? null : projectFirstTouchResolution(snapshot, policy);
}

function toPolicy(config: ResolutionConfig): ResolutionThresholdDecayPolicy | null {
  if ('thresholdFloorPpb' in config) return config;
  try {
    return {
      initialThresholdPpb: config.initialThresholdPpb,
      thresholdFloorPpb: resolutionThresholdFloorPpb(config.outcomeCount, config.floorMarginPpb),
      tradingStartedTs: config.tradingStartedTs,
      decayDelaySecs: config.decayDelaySecs,
      dropPpb: config.dropPpb,
      dropIntervalSecs: config.dropIntervalSecs,
    };
  } catch {
    return null;
  }
}
